/**
 * History page — scoped ledger view (one asset or action per Me icon entry).
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { usersAPI } from '../api/users';
import { LoadingSkeleton } from '../components/ui/LoadingSkeleton';
import { EmptyState } from '../components/ui/EmptyState';
import { ThemedPageHeader } from '../components/ui/ThemedPageHeader';
import { ThemedFixedTopBar, ThemedPanel } from '../components/ui/ThemedSurface';
import { HISTORY_COLORS as C, TRADING_COLORS, PROD_HIDDEN_HISTORY_ASSETS, PROD_HIDDEN_HISTORY_ACTIONS } from '@/constants';
import { QueryErrorNote } from '../components/ui/QueryErrorNote';
import { LedgerHistoryRow } from '../components/history/LedgerHistoryRow';
import {
  formatLedgerAction,
  normalizeLedgerActionQuery,
} from '../utils/ledger-display';
import { tokenDisplayLabel } from '@/utils/orb-display';

interface LedgerRow {
  id: string;
  asset: string;
  action: string;
  delta: string;
  balance_after: string;
  ref: string;
  note: string | null;
  create_time: string;
}

interface LedgerResponse {
  total: number;
  rows: LedgerRow[];
}

const PAGE_SIZE = 20;
const LEDGER_STALE_MS = 60_000;

function PageButton({ children, disabled, onClick }: { children: ReactNode; disabled: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className="h-9 rounded-lg border px-3 text-sm font-semibold transition active:scale-[0.98] disabled:opacity-45"
      style={{ borderColor: C.softBorder, background: C.chipBg, color: C.text }}
    >
      {children}
    </button>
  );
}

function ScopeLabel({ label }: { label: string }) {
  return (
    <div
      className="flex h-10 w-full items-center justify-center rounded-lg border px-3 text-xs font-semibold"
      style={{ borderColor: C.softBorder, background: C.innerBg, color: C.gold }}
    >
      <span className="truncate">{label}</span>
    </div>
  );
}

function ledgerQueryKey(page: number, asset: string, action: string) {
  return ['user', 'ledger', page, asset, action] as const;
}

function resolveHistoryScope(searchParams: URLSearchParams) {
  const rawAsset = searchParams.get('asset') ?? '';
  const rawAction = normalizeLedgerActionQuery(searchParams.get('action') ?? '');
  const asset = PROD_HIDDEN_HISTORY_ASSETS.has(rawAsset) ? '' : rawAsset;
  const action = PROD_HIDDEN_HISTORY_ACTIONS.has(rawAction) ? '' : rawAction;
  return { asset, action };
}

export function History() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const [page, setPage] = useState(1);

  const { asset, action } = useMemo(() => resolveHistoryScope(searchParams), [searchParams]);
  const hasScope = Boolean(asset || action);

  const scopeLabel = useMemo(() => {
    if (asset) return tokenDisplayLabel(asset);
    if (action) return formatLedgerAction(action, t);
    return '';
  }, [asset, action, t]);

  useEffect(() => {
    if (!hasScope) {
      navigate('/me', { replace: true });
    }
  }, [hasScope, navigate]);

  useEffect(() => {
    setPage(1);
  }, [asset, action]);

  const { data, isLoading, isFetching, isError, refetch } = useQuery({
    queryKey: ledgerQueryKey(page, asset, action),
    queryFn: async (): Promise<LedgerResponse> =>
      (await usersAPI.getLedger({
        page,
        limit: PAGE_SIZE,
        ...(asset ? { asset } : {}),
        ...(action ? { action } : {}),
      })) as unknown as LedgerResponse,
    enabled: hasScope,
    placeholderData: (prev) => prev,
    staleTime: LEDGER_STALE_MS,
    gcTime: 5 * 60_000,
  });

  const ledgerRows = data?.rows ?? [];
  const totalPages = Math.max(1, Math.ceil((data?.total ?? 0) / PAGE_SIZE));
  const balanceAfterLabel = t('history.balanceAfter', '余');
  const showInitialSkeleton = isLoading && !data;
  const showFetchingOverlay = isFetching && Boolean(data);

  useEffect(() => {
    if (!hasScope || page >= totalPages) return;
    void queryClient.prefetchQuery({
      queryKey: ledgerQueryKey(page + 1, asset, action),
      queryFn: () =>
        usersAPI.getLedger({
          page: page + 1,
          limit: PAGE_SIZE,
          ...(asset ? { asset } : {}),
          ...(action ? { action } : {}),
        }) as Promise<LedgerResponse>,
      staleTime: LEDGER_STALE_MS,
    });
  }, [page, totalPages, asset, action, queryClient, hasScope]);

  if (!hasScope) {
    return null;
  }

  return (
    <div className="relative min-h-full pb-8" style={{ background: C.pageBg }}>
      <div className="relative mx-auto min-h-full w-full max-w-[430px]">
        <ThemedPageHeader title={t('history.title', '交易记录')} onBack={() => navigate(-1)} theme={C} />

        <ThemedFixedTopBar background={TRADING_COLORS.fixedTopBg} className="pb-2 pt-2">
          <ScopeLabel label={scopeLabel} />
        </ThemedFixedTopBar>

        <div className="px-5 pt-[112px]">
          <div className="flex flex-col gap-2">
            {isError && (
              <QueryErrorNote
                message={t('history.loadFailed', '加载交易记录失败')}
                onRetry={() => void refetch()}
              />
            )}
            {data && (
              <div className="flex items-center gap-2 px-0.5 text-xs" style={{ color: C.muted }}>
                <span>{t('history.totalRecords', '共 {{count}} 条记录', { count: data.total })}</span>
                {showFetchingOverlay ? (
                  <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent opacity-70" />
                ) : null}
              </div>
            )}
            {showInitialSkeleton ? (
              <LoadingSkeleton variant="list" lines={6} className="border" />
            ) : ledgerRows.length === 0 ? (
              <ThemedPanel theme={C}>
                <EmptyState
                  icon={
                    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M8 7h8M8 11h8M8 15h5" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h10l6 6v10a2 2 0 0 1-2 2z" />
                    </svg>
                  }
                  title={t('history.noRecords', '暂无记录')}
                  description={t('history.noRecordsHint', '交易记录会显示在这里。')}
                />
              </ThemedPanel>
            ) : (
              <ThemedPanel theme={C} className={showFetchingOverlay ? 'opacity-80 transition-opacity' : undefined}>
                {ledgerRows.map((row, idx) => (
                  <LedgerHistoryRow
                    key={row.id}
                    row={row}
                    showBorder={idx < ledgerRows.length - 1}
                    theme={C}
                    balanceAfterLabel={balanceAfterLabel}
                    t={t}
                  />
                ))}
              </ThemedPanel>
            )}

            {totalPages > 1 && (
              <div className="mx-4 mt-3 flex items-center justify-center gap-4">
                <PageButton disabled={page <= 1 || isFetching} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                  &lt; {t('history.prevPage', '上一页')}
                </PageButton>
                <span className="text-sm" style={{ color: C.muted }}>
                  {page} / {totalPages}
                </span>
                <PageButton
                  disabled={page >= totalPages || isFetching}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                >
                  {t('history.nextPage', '下一页')} &gt;
                </PageButton>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
